import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import WebApp from '@twa-dev/sdk';
import LoadingScreen from './components/LoadingScreen';
import BottomNav from './components/BottomNav';
import OnboardingSimple from './components/OnboardingSimple';
import ErrorBoundary from './components/ErrorBoundary';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import DebugPanel from './components/DebugPanel';
import BrowserLogin from './components/BrowserLogin';
import AdminLogin from './components/AdminLogin';
import AddExpenseModal from './components/AddExpenseModal';
import PageLoader from './components/PageLoader';
import DashboardSkeleton from './components/DashboardSkeleton';
import OfflineBanner from './components/OfflineBanner';
import { authService } from './services/authService';
import { apiService } from './services/apiService';
import { analytics } from './services/analytics';
import { logger } from './utils/logger';
import { deferUntilIdle } from './utils/requestPriority';
import { offlineService } from './services/offlineService';
import * as serviceWorkerRegistration from './services/serviceWorkerRegistration';
import { User } from './types';
import { ToastProvider, useToast } from './hooks/useToast';
import { AppProvider } from './contexts/AppContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import './styles/global.css';

// ⚡ PERFORMANCE: Lazy load TON Connect + Pages
const TonConnectProvider = lazy(() => import('./components/TonConnectProvider'));
const Dashboard = lazy(() => import('./components/DashboardModern'));
const Groups = lazy(() => import('./pages/Groups'));
const GroupDetail = lazy(() => import('./pages/GroupDetail'));
const AddExpense = lazy(() => import('./pages/AddExpense'));
const ExpenseDetail = lazy(() => import('./pages/ExpenseDetail'));
const Profile = lazy(() => import('./pages/Profile'));
const Insights = lazy(() => import('./pages/Insights'));
const ExpensesPage = lazy(() => import('./pages/ExpensesPage'));
const SettleUpPage = lazy(() => import('./pages/SettleUpPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const DebugPage = lazy(() => import('./pages/DebugPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboardEnterprise'));

// Page view tracker component
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    analytics.trackPageView(location.pathname + location.search);
  }, [location]);

  // ⚡ PREFETCH: Preload all pages immediately after initial load for instant navigation
  useEffect(() => {
    // Preload all route components in the background for instant navigation
    const preloadAllPages = async () => {
      // Small delay to not interfere with current page load
      await new Promise(resolve => setTimeout(resolve, 500));
      
      logger.debug('🔮 Preloading all pages for instant navigation');
      
      // Preload all main navigation pages
      Promise.all([
        import('./pages/ExpensesPage'),
        import('./pages/SettleUpPage'),
        import('./pages/SettingsPage'),
        import('./pages/Insights'),
        import('./pages/AddExpense'),
        import('./pages/Groups'),
        import('./pages/Profile'),
      ]).catch(err => {
        logger.debug('Prefetch error (non-critical):', err);
      });
    };

    preloadAllPages();
  }, []); // Only run once on mount

  return null;
}

function AppContent() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [showBrowserLogin, setShowBrowserLogin] = useState(false);
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [forcePersonalGroup, setForcePersonalGroup] = useState<string | undefined>(undefined);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [offlineQueueCount, setOfflineQueueCount] = useState(0);
  const { addToast } = useToast();

  // Keyboard shortcut to toggle debug panel (Ctrl+Shift+D or Cmd+Shift+D)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setShowDebug(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ⚡ SERVICE WORKER: Register and handle offline/online events
  useEffect(() => {
    // Register service worker
    serviceWorkerRegistration.register({
      onSuccess: () => {
        logger.info('✅ App ready for offline use');
      },
      onUpdate: (registration) => {
        logger.info('🆕 New version available');
        addToast({
          type: 'info',
          message: 'New version available! Tap to update.',
          duration: 10000,
          action: {
            label: 'Update',
            onClick: () => {
              if (registration.waiting) {
                registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                window.location.reload();
              }
            }
          }
        });
      },
      onOffline: () => {
        setIsOffline(true);
        addToast({
          type: 'warning',
          message: '📵 Offline mode - Expenses will sync when back online',
          duration: 5000
        });
      },
      onOnline: async () => {
        setIsOffline(false);
        addToast({
          type: 'success',
          message: '🌐 Back online!',
          duration: 3000
        });
        
        // Sync offline expenses
        try {
          const result = await offlineService.syncNow();
          if (result.success > 0) {
            addToast({
              type: 'success',
              message: `✅ Synced ${result.success} offline expense${result.success > 1 ? 's' : ''}`,
              duration: 5000
            });
            setOfflineQueueCount(0);
          }
        } catch (error) {
          logger.error('Failed to sync offline expenses:', error);
        }
      }
    });

    // Initialize offline service
    offlineService.init().then(() => {
      logger.debug('✅ Offline service initialized');
      // Check queue count
      offlineService.getQueueCount().then(count => {
        setOfflineQueueCount(count);
        if (count > 0) {
          logger.info(`📤 ${count} expense${count > 1 ? 's' : ''} queued for sync`);
        }
      });
    });

    // Update queue count when expenses are added offline
    const handleOfflineExpenseAdded = () => {
      offlineService.getQueueCount().then(count => {
        setOfflineQueueCount(count);
      });
    };

    window.addEventListener('offline-expense-added', handleOfflineExpenseAdded);
    return () => {
      window.removeEventListener('offline-expense-added', handleOfflineExpenseAdded);
    };
  }, [addToast]);

  // Auto-detect and apply dark/light theme based on system preference
  useEffect(() => {
    const applyTheme = () => {
      const root = document.documentElement;
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      
      if (prefersDark) {
        root.classList.add('dark-theme');
      } else {
        root.classList.remove('dark-theme');
      }
    };

    // Apply theme on mount
    applyTheme();

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleThemeChange = () => applyTheme();
    
    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleThemeChange);
      return () => mediaQuery.removeEventListener('change', handleThemeChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleThemeChange);
      return () => mediaQuery.removeListener(handleThemeChange);
    }
  }, []);

  useEffect(() => {
    const initApp = async () => {
      try {
        // 🔄 Version check - Force user data refresh if structure changed
        const APP_DATA_VERSION = '2.0'; // Increment to force refresh
        const storedVersion = localStorage.getItem('appDataVersion');
        if (storedVersion !== APP_DATA_VERSION) {
          logger.info('🔄 App data version mismatch - clearing user cache');
          localStorage.removeItem('currentUser');
          localStorage.setItem('appDataVersion', APP_DATA_VERSION);
        }

        // ⚡ PERFORMANCE: Defer analytics initialization until browser is idle
        deferUntilIdle(() => {
          analytics.initialize();
          logger.debug('📊 Analytics initialized (deferred)');
        });

        // Check if we're on admin route - skip Telegram checks entirely
        const isAdminRoute = window.location.pathname === '/admin';
        if (isAdminRoute) {
          logger.info('🛡️ Admin route detected - checking for existing token');
          const token = localStorage.getItem('authToken');
          const storedUser = localStorage.getItem('currentUser');
          
          if (token && storedUser) {
            try {
              const user = JSON.parse(storedUser);
              setUser(user);
              logger.info('✅ Admin authenticated via stored token');
            } catch (e) {
              logger.warn('Failed to parse stored user');
              setShowBrowserLogin(true);
            }
          } else {
            logger.info('❌ No token found - showing admin login');
            setShowBrowserLogin(true);
          }
          
          setLoading(false);
          return;
        }

        // Check if we're accessing the debug page OR if debug mode is enabled
        const isDebugPage = window.location.pathname === '/debug';
        const debugMode = window.location.search.includes('debug=true') || localStorage.getItem('debugMode') === 'true';
        
        // Track app opened with source parameter (from bot buttons)
        const urlParams = new URLSearchParams(window.location.search);
        const source = urlParams.get('source');
        const chatId = urlParams.get('chat');
        if (source || chatId) {
          analytics.trackAppOpened(source || undefined, chatId || undefined);
        }
        
        // Handle deep link from Telegram startapp parameter (e.g., /settle command)
        // Format: settle_GROUP_ID_TO_USER_ID_AMOUNT
        try {
          const telegramWebApp = window.Telegram?.WebApp;
          if (telegramWebApp && (telegramWebApp.initDataUnsafe as any)?.start_param) {
            const startParam = (telegramWebApp.initDataUnsafe as any).start_param;
            logger.debug('📲 Deep link detected:', startParam);
            
            if (startParam.startsWith('settle_')) {
              // Parse: settle_GROUP_ID_TO_USER_ID_AMOUNT
              const parts = startParam.split('_');
              logger.debug('📲 Deep link parts:', parts);
              
              if (parts.length >= 4) {
                const groupId = parts[1];
                const toUserId = parts[2];
                const amount = parts[3];
                
                logger.debug('📲 Parsed deep link:', { groupId, toUserId, amount });
                
                // Navigate to settle page with query params after user loads
                // Will be picked up by SettleUpPage
                setTimeout(() => {
                  logger.debug('📲 Navigating to settle page with params');
                  window.location.href = `/settle?group=${groupId}&to=${toUserId}&amount=${amount}&fromDeepLink=true`;
                }, 500);
              } else {
                logger.warn('📲 Invalid deep link format, expected 4+ parts, got:', parts.length);
              }
            }
          }
        } catch (deepLinkError) {
          logger.error('📲 Deep link error:', deepLinkError);
        }
        
        // Check if we're in a browser (preview mode) or Telegram Web App
        // Check for Telegram.WebApp existence, not initData (initData might be empty initially)
        const isTelegramWebApp = typeof window !== 'undefined' && 
          window.Telegram && 
          window.Telegram.WebApp;

        if (isDebugPage) {
          // Allow debug page to load without Telegram
          setLoading(false);
          return;
        }

        // If debug mode is enabled, skip Telegram redirect and show browser login
        if (debugMode && !isTelegramWebApp) {
          logger.debug('Debug mode enabled');
          localStorage.setItem('debugMode', 'true');
          
          // Check if we already have a valid session
          const storedUser = localStorage.getItem('currentUser');
          const storedToken = localStorage.getItem('authToken');
          
          if (storedUser && storedToken && storedToken !== 'mock-token-for-debug') {
            logger.debug('Using existing session');
            setUser(JSON.parse(storedUser));
            setLoading(false);
            return;
          }
          
          // Show browser login screen
          logger.debug('Showing browser login...');
          setShowBrowserLogin(true);
          setLoading(false);
          return;
        }

        if (isTelegramWebApp) {
          // Initialize Telegram Web App
          WebApp.ready();
          WebApp.expand();
          
          // ✅ Enable pull-to-refresh by disabling Telegram's default swipe-to-close
          if (WebApp.disableVerticalSwipes) {
            WebApp.disableVerticalSwipes();
            logger.debug('✅ Pull-to-refresh enabled (vertical swipes disabled)');
          } else {
            logger.warn('⚠️  disableVerticalSwipes not available in this Telegram version');
          }
          
          // Enable fullscreen mode for immersive experience
          if (WebApp.isVersionAtLeast && WebApp.isVersionAtLeast('6.1')) {
            try {
              if (WebApp.requestFullscreen) {
                WebApp.requestFullscreen();
                logger.debug('Fullscreen mode enabled');
              }
            } catch (error) {
              logger.debug('Fullscreen mode not supported on this version');
            }
          }
          
          // Set optimal viewport settings to match app theme
          if (WebApp.setHeaderColor) {
            WebApp.setHeaderColor('#667eea');
          }
          if (WebApp.setBackgroundColor) {
            WebApp.setBackgroundColor('#f5f6fa');
          }

          // Get user data from Telegram
          // Try initData first, fallback to constructing from initDataUnsafe if needed
          let initData = WebApp.initData;
          
          // If initData is empty, try to construct it from initDataUnsafe
          if (!initData && WebApp.initDataUnsafe?.user) {
            logger.debug('⚠️ initData is empty, attempting to construct from initDataUnsafe');
            const unsafe = WebApp.initDataUnsafe;
            const userJson = JSON.stringify(unsafe.user);
            const authDate = unsafe.auth_date || Math.floor(Date.now() / 1000);
            // Note: We can't construct the hash without the secret, so this might not work
            // But we can try authenticating with the user data directly
            initData = `user=${encodeURIComponent(userJson)}&auth_date=${authDate}`;
          }
          
          logger.debug('🔍 Telegram WebApp detected:', {
            hasInitData: !!WebApp.initData,
            initDataLength: WebApp.initData?.length || 0,
            hasConstructedInitData: !!initData && initData !== WebApp.initData,
            initDataUnsafe: WebApp.initDataUnsafe
          });
          
          if (initData && initData.length > 0) {
            // Authenticate (show skeleton while loading)
            try {
              logger.debug('🔐 Authenticating with Telegram initData...');
              const userData = await authService.authenticate(initData);
              logger.debug('✅ Authentication successful');
              
              setUser(userData.user);
              
              // Store user and auth token in localStorage
              localStorage.setItem('currentUser', JSON.stringify(userData.user));
              localStorage.setItem('authToken', userData.token);
              
              // ⚡ PERFORMANCE: Defer analytics tracking (non-critical)
              deferUntilIdle(() => {
                analytics.setUserProperties(userData.user.id, {
                  username: userData.user.username,
                  firstName: userData.user.firstName,
                  isPremium: userData.user.isPremium,
                  languageCode: userData.user.languageCode
                });
              });
              
              // Check if user has completed onboarding (server-side)
              if (!userData.user.hasCompletedOnboarding) {
                setShowOnboarding(true);
              }
              
              // ⚡ Show UI after auth completes
              setLoading(false);
            } catch (err: any) {
              logger.error('❌ Authentication failed:', err);
              logger.error('Error details:', {
                message: err?.message,
                response: err?.response?.data,
                status: err?.response?.status
              });
              setLoading(false);
              // Show error message to user
              setError('Authentication failed. Please try reopening the app from Telegram.');
            }
          } else {
            logger.warn('⚠️ Telegram WebApp detected but initData is missing');
            logger.warn('WebApp state:', {
              version: WebApp.version,
              platform: WebApp.platform,
              initDataUnsafe: WebApp.initDataUnsafe
            });
            setLoading(false);
            setError('Telegram authentication data is missing. Please reopen the app from Telegram.');
          }
        } else {
          // Web preview mode - redirect to Telegram
          logger.debug('Web preview mode detected - redirecting to Telegram');
          
          // Show a message and redirect to Telegram bot
          const botUsername = process.env.REACT_APP_BOT_USERNAME || 'settlerx_bot';
          const redirectUrl = `https://t.me/${botUsername}`;
          
          // Create a simple redirect page
          document.body.innerHTML = `
            <div style="
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              height: 100vh;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              text-align: center;
              padding: 20px;
            ">
              <div style="
                background: rgba(255, 255, 255, 0.1);
                backdrop-filter: blur(10px);
                border-radius: 20px;
                padding: 40px;
                max-width: 500px;
                box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
              ">
                <h1 style="font-size: 48px; margin: 0 0 20px 0;">🚀 Settler</h1>
                <h2 style="font-size: 24px; margin: 0 0 20px 0; font-weight: 500;">Smart Expense Splitting</h2>
                <p style="font-size: 18px; margin: 0 0 30px 0; opacity: 0.9;">
                  This app is designed to work inside Telegram.
                </p>
                <a href="${redirectUrl}" style="
                  display: inline-block;
                  background: white;
                  color: #667eea;
                  padding: 15px 40px;
                  border-radius: 10px;
                  text-decoration: none;
                  font-weight: 600;
                  font-size: 18px;
                  transition: transform 0.2s;
                " onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
                  Open in Telegram
                </a>
                <p style="font-size: 14px; margin: 30px 0 0 0; opacity: 0.7;">
                  Add @${botUsername} to your group chat to get started
                </p>
              </div>
            </div>
          `;
          
          // Auto-redirect after 3 seconds
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 3000);
          
          return;
        }
      } catch (error) {
        logger.error('Failed to initialize app', error);
        
        // If authentication fails, redirect to Telegram
        const botUsername = process.env.REACT_APP_BOT_USERNAME || 'settlerx_bot';
        const redirectUrl = `https://t.me/${botUsername}`;
        
        document.body.innerHTML = `
          <div style="
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100vh;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            text-align: center;
            padding: 20px;
          ">
            <div style="
              background: rgba(255, 255, 255, 0.1);
              backdrop-filter: blur(10px);
              border-radius: 20px;
              padding: 40px;
              max-width: 500px;
              box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
            ">
              <h1 style="font-size: 48px; margin: 0 0 20px 0;">⚠️</h1>
              <h2 style="font-size: 24px; margin: 0 0 20px 0; font-weight: 500;">Authentication Required</h2>
              <p style="font-size: 18px; margin: 0 0 30px 0; opacity: 0.9;">
                Please open this app inside Telegram.
              </p>
              <a href="${redirectUrl}" style="
                display: inline-block;
                background: white;
                color: #667eea;
                padding: 15px 40px;
                border-radius: 10px;
                text-decoration: none;
                font-weight: 600;
                font-size: 18px;
                transition: transform 0.2s;
              " onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
                Open in Telegram
              </a>
            </div>
          </div>
        `;
        
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 3000);
      }
      // Note: setLoading(false) is now called immediately in the Telegram flow for instant UI
    };

    initApp();
  }, []);

  // 📡 WebSocket listener for real-time premium status updates
  useEffect(() => {
    if (!user) return;

    // Import WebSocket
    import('socket.io-client').then(({ io }) => {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 
                        (window.location.hostname === 'localhost' 
                          ? 'http://localhost:3001' 
                          : 'https://backend-production-afd4.up.railway.app');

      logger.info('📡 Connecting WebSocket for premium updates');

      const socket = io(backendUrl, {
        auth: { token },
        reconnection: true,
        transports: ['websocket', 'polling'],
      });

      socket.on('connect', () => {
        logger.info('✅ WebSocket connected for premium updates');
      });

      // Listen for premium status updates
      socket.on('premium:updated', (premiumData: any) => {
        logger.info('📡 Received premium status update:', premiumData);
        
        // Update user state
        setUser((prevUser) => {
          if (!prevUser) return prevUser;
          
          const updatedUser = {
            ...prevUser,
            premiumTier: premiumData.premiumTier,
            premiumExpiresAt: premiumData.premiumExpiresAt,
            premiumActivatedAt: premiumData.premiumActivatedAt,
          };
          
          // Update localStorage
          localStorage.setItem('currentUser', JSON.stringify(updatedUser));
          
          return updatedUser;
        });
        
        // Show notification
        const tierName = premiumData.premiumTier === 'lifetime' 
          ? '👑 Lifetime Premium' 
          : premiumData.premiumTier === 'premium' 
            ? '✨ Premium' 
            : '🆓 Free';
        
        addToast({
          type: 'success',
          message: `Your premium status has been updated to ${tierName}!`,
          duration: 5000,
        });
      });

      socket.on('disconnect', (reason) => {
        logger.warn(`🔌 WebSocket disconnected: ${reason}`);
      });

      // Cleanup
      return () => {
        logger.info('🔌 Cleaning up premium WebSocket');
        socket.disconnect();
      };
    });
  }, [user, addToast]);

  const handleAddExpense = async () => {
    // Check if user is on Personal view
    const currentView = localStorage.getItem('currentDashboardView');
    
    if (currentView === 'personal') {
      // Fetch Personal group using dedicated endpoint
      try {
        const personalGroup = await apiService.getPersonalGroup();
        
        if (personalGroup) {
          setForcePersonalGroup(personalGroup.id);
        } else {
          setForcePersonalGroup(undefined);
        }
      } catch (error) {
        logger.error('Failed to fetch Personal group:', error);
        setForcePersonalGroup(undefined);
      }
    } else {
      setForcePersonalGroup(undefined);
    }
    
    setShowAddExpenseModal(true);
  };

  const handleBrowserLogin = (token: string, userData: User) => {
    logger.debug('✅ Browser login successful:', userData);
    setUser(userData);
    localStorage.setItem('currentUser', JSON.stringify(userData));
    localStorage.setItem('authToken', token);
    setShowBrowserLogin(false);
  };

  const handleOnboardingComplete = async () => {
    try {
      // Call backend to persist onboarding completion
      const authToken = localStorage.getItem('authToken');
      if (authToken && authToken !== 'preview_mode_token') {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/me/onboarding`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          // Update user state with new onboarding status
          if (user) {
            const updatedUser = { ...user, hasCompletedOnboarding: data.hasCompletedOnboarding };
            setUser(updatedUser);
            localStorage.setItem('currentUser', JSON.stringify(updatedUser));
          }
        }
      }
      
      // Fallback: also set localStorage for preview mode
      localStorage.setItem('hasSeenOnboarding', 'true');
      setShowOnboarding(false);
    } catch (error) {
      logger.error('Failed to complete onboarding:', error);
      // Still hide onboarding even if API call fails
      localStorage.setItem('hasSeenOnboarding', 'true');
      setShowOnboarding(false);
    }
  };

  if (loading) {
    return (
      <div className="app">
        <LoadingScreen />
      </div>
    );
  }

  if (showBrowserLogin) {
    // Show AdminLogin for /admin route, regular BrowserLogin for others
    const isAdminRoute = window.location.pathname === '/admin';
    if (isAdminRoute) {
      return <AdminLogin onLogin={handleBrowserLogin} />;
    }
    return <BrowserLogin onLogin={handleBrowserLogin} />;
  }

  if (showOnboarding && user) {
    return <OnboardingSimple onComplete={handleOnboardingComplete} />;
  }

  // Special case: Debug page doesn't require user authentication (dev only)
  const isDebugPage = window.location.pathname === '/debug';
  if (isDebugPage && process.env.NODE_ENV !== 'production') {
    return (
      <div className="app">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/debug" element={<DebugPage />} />
          </Routes>
        </Suspense>
      </div>
    );
  }

  if (!user && !loading) {
    return (
      <div className="app">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            padding: '40px',
            maxWidth: '500px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
          }}>
            <h1 style={{ fontSize: '48px', margin: '0 0 20px 0' }}>⚠️</h1>
            <h2 style={{ fontSize: '24px', margin: '0 0 20px 0', fontWeight: 500 }}>
              Authentication Required
            </h2>
            <p style={{ fontSize: '18px', margin: '0 0 30px 0', opacity: 0.9 }}>
              {error || 'Please open this app through Telegram to continue.'}
            </p>
            <button
              onClick={() => {
                // Try to reload
                window.location.reload();
              }}
              style={{
                background: 'white',
                color: '#667eea',
                padding: '15px 40px',
                border: 'none',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: '18px',
                cursor: 'pointer',
                transition: 'transform 0.2s'
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Offline Banner */}
      {isOffline && <OfflineBanner queueCount={offlineQueueCount} />}
      
      {/* Track page views on route changes */}
      <PageViewTracker />
      
      <main className="main-content">
        <RouteErrorBoundary>
          <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
            <Routes>
              <Route path="/" element={user ? <Dashboard user={user} /> : <DashboardSkeleton />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/expenses" element={user ? <ExpensesPage user={user} /> : <PageLoader />} />
              <Route 
                path="/settle" 
                element={
                  user ? (
                    <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
                      <TonConnectProvider>
                        <SettleUpPage user={user} />
                      </TonConnectProvider>
                    </Suspense>
                  ) : <PageLoader />
                } 
              />
              <Route 
                path="/settings" 
                element={
                  user ? (
                    <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
                      <TonConnectProvider>
                        <SettingsPage user={user} />
                      </TonConnectProvider>
                    </Suspense>
                  ) : <PageLoader />
                } 
              />
              {/* Legacy routes for backward compatibility */}
              <Route path="/groups" element={user ? <Groups user={user} /> : <PageLoader />} />
              <Route path="/groups/:id" element={user ? <GroupDetail user={user} /> : <PageLoader />} />
              <Route path="/expenses/add" element={user ? <AddExpense user={user} /> : <PageLoader />} />
              <Route path="/expenses/:id" element={user ? <ExpenseDetail user={user} /> : <PageLoader />} />
              <Route path="/profile" element={user ? <Profile user={user} /> : <PageLoader />} />
              <Route path="/insights" element={user ? <Insights user={user} /> : <PageLoader />} />
              {/* Debug page - only in development */}
              {process.env.NODE_ENV !== 'production' && (
                <Route path="/debug" element={<DebugPage />} />
              )}
            </Routes>
          </Suspense>
        </RouteErrorBoundary>
      </main>

      <BottomNav onAddExpense={handleAddExpense} />
      
      {/* Add Expense Modal */}
      {user && (
        <AddExpenseModal 
          user={user}
          isOpen={showAddExpenseModal}
          onClose={() => {
            setShowAddExpenseModal(false);
            setForcePersonalGroup(undefined);
          }}
          forceGroupId={forcePersonalGroup}
        />
      )}
      
      {/* Debug panel - toggle with Ctrl+Shift+D or Cmd+Shift+D */}
      {showDebug && <DebugPanel />}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <WebSocketProvider>
        <AppProvider>
          <ToastProvider>
            <Router>
              <AppContent />
            </Router>
          </ToastProvider>
        </AppProvider>
      </WebSocketProvider>
    </ErrorBoundary>
  );
}

export default App;